import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SignOut, EnvelopeSimple, Eye, PaperPlaneTilt, Crown, ArrowRight, ShieldCheck, ChartBar, Lock, EyeSlash, BellRinging, BellSlash, Suitcase, Lifebuoy, Trophy } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import {
  isPushSupported, pushPermissionState, getActiveSubscription,
  subscribeToPush, unsubscribeFromPush, sendTestPush,
} from "@/lib/push";
import EditProfileSection from "@/components/EditProfileSection";

export default function Profile() {
  const { user, logout, refreshUser, changePassword } = useAuth();
  const [optOut, setOptOut] = useState(!!user?.email_digest_opt_out);
  const [busy, setBusy] = useState(false);
  const [proInfo, setProInfo] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Change-password panel state
  const [cpOpen, setCpOpen] = useState(false);
  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpConfirm, setCpConfirm] = useState("");
  const [cpShow, setCpShow] = useState(false);
  const [cpBusy, setCpBusy] = useState(false);
  // Push notifications state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPerm, setPushPerm] = useState("default");
  const [pushSubbed, setPushSubbed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  // Live-visibility state ("Appear on live course")
  const [liveVisible, setLiveVisible] = useState(user?.live_visible !== false);
  const [liveBusy, setLiveBusy] = useState(false);

  useEffect(() => {
    api.get("/pro/me").then((r) => setProInfo(r.data)).catch(() => {});
    api.get("/admin/whoami").then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => {});
    setPushSupported(isPushSupported());
    setPushPerm(pushPermissionState());
    getActiveSubscription().then((s) => setPushSubbed(!!s));
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushSubbed) {
        const r = await unsubscribeFromPush();
        if (r.ok) { setPushSubbed(false); toast.success("Push notifications turned off"); }
      } else {
        const r = await subscribeToPush();
        if (r.ok) {
          setPushSubbed(true);
          setPushPerm("granted");
          toast.success("Push notifications on — try the Test button to verify");
        } else if (r.reason === "permission_denied") {
          toast.error("Permission denied. Enable notifications in browser settings.");
        } else if (r.reason === "unsupported") {
          toast.error("Push isn't supported on this browser/device.");
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    setPushBusy(true);
    const r = await sendTestPush();
    setPushBusy(false);
    if (r.ok && r.sent > 0) toast.success(`Test sent to ${r.sent} device${r.sent === 1 ? "" : "s"}`);
    else if (r.ok && r.expired) toast.error("Your subscription expired — toggle off then on.");
    else toast.error(r.error || "Send failed");
  };

  const toggleLive = async () => {
    const next = !liveVisible;
    setLiveBusy(true);
    try {
      await api.post("/live/prefs", { live_visible: next });
      setLiveVisible(next);
      toast.success(next ? "You'll appear on live course" : "Hidden from live course");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLiveBusy(false);
    }
  };

  const toggleOptOut = async () => {
    const next = !optOut;
    setOptOut(next);
    try {
      await api.post("/digest/prefs", { opt_out: next });
      toast.success(next ? "Email digest paused" : "Email digest resumed");
      if (refreshUser) refreshUser();
    } catch {
      setOptOut(!next);
      toast.error("Couldn't update preference");
    }
  };

  const openPreview = async () => {
    setBusy(true);
    try {
      const r = await api.get("/digest/preview", { responseType: "text" });
      const w = window.open("", "_blank");
      w.document.open(); w.document.write(r.data); w.document.close();
    } catch (e) {
      toast.error("Couldn't load preview");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/digest/send-test");
      if (r.data.sent) toast.success(`Digest sent — check ${user.email}`);
      else if (r.data.reason === "no_activity") toast.info("No activity in the last 7 days — play a round first");
      else toast.error(`Couldn't send: ${r.data.reason}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="rise">
        <span className="label-eyebrow">Profile</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Your card</h1>
      </div>

      <EditProfileSection />

      <Link to="/bag" data-testid="link-to-my-bag" className="card-interactive p-5 flex items-center justify-between gap-3 group">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-[#F8F7F3] text-[#0F2537] flex items-center justify-center shrink-0">
            <Suitcase weight="duotone" size={22} />
          </div>
          <div className="min-w-0">
            <span className="label-eyebrow">Equipment</span>
            <h2 className="font-heading font-bold text-lg mt-0.5">My bag</h2>
            <p className="text-xs text-[#0F2537]/60">Set the clubs you carry and your distances — used for on-course suggestions.</p>
          </div>
        </div>
        <ArrowRight weight="bold" size={18} className="text-[#0F2537]/40 group-hover:text-[#E05D3A] transition" />
      </Link>

      <Link to="/help" data-testid="profile-help-link" className="card-interactive p-5 flex items-center justify-between gap-3 group">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-[#F8F7F3] text-[#0F2537] flex items-center justify-center shrink-0">
            <Lifebuoy weight="duotone" size={22} />
          </div>
          <div className="min-w-0">
            <span className="label-eyebrow">Help</span>
            <h2 className="font-heading font-bold text-lg mt-0.5">How-to guide</h2>
            <p className="text-xs text-[#0F2537]/60">Recording rounds, GPS rangefinder, smart club pick, tournaments &amp; more.</p>
          </div>
        </div>
        <ArrowRight weight="bold" size={18} className="text-[#0F2537]/40 group-hover:text-[#E05D3A] transition" />
      </Link>

      <Link to="/badges" data-testid="profile-badges-link" className="card-interactive p-5 flex items-center justify-between gap-3 group">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-[#E05D3A]/10 text-[#E05D3A] flex items-center justify-center shrink-0">
            <Trophy weight="duotone" size={22} />
          </div>
          <div className="min-w-0">
            <span className="label-eyebrow">Achievements</span>
            <h2 className="font-heading font-bold text-lg mt-0.5">Badges</h2>
            <p className="text-xs text-[#0F2537]/60">Eagles, break-80s, no-double-bogey rounds &mdash; collect them all.</p>
          </div>
        </div>
        <ArrowRight weight="bold" size={18} className="text-[#0F2537]/40 group-hover:text-[#E05D3A] transition" />
      </Link>

      {/* Bula Pro section */}
      <div className={`card-default p-7 ${user?.is_pro ? "border-[#E05D3A]" : ""}`} data-testid="pro-section">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${user?.is_pro ? "bg-[#E05D3A] text-white" : "bg-[#F8F7F3] text-[#0F2537]"}`}>
              <Crown weight="fill" size={22} />
            </div>
            <div>
              <span className="label-eyebrow">Bula Pro</span>
              <h2 className="font-heading font-bold text-xl mt-0.5">
                {user?.is_pro ? "You're Pro" : "Upgrade to Pro"}
              </h2>
              {user?.is_pro ? (
                <div className="text-xs text-[#0F2537]/70 mt-1" data-testid="pro-status">
                  Plan: <b>{user.pro_plan}</b> · until {new Date(user.pro_expires_at).toLocaleDateString()}
                </div>
              ) : (
                <p className="text-sm text-[#0F2537]/70 mt-1">Club averages, ad-free shareables, premium tournaments.</p>
              )}
            </div>
          </div>
          <Link to="/pricing" data-testid="profile-pricing-link"
            className={`px-4 py-2.5 rounded-xl font-bold text-sm inline-flex items-center gap-1 ${
              user?.is_pro ? "border border-[#E2E0D8] text-[#0F2537]" : "bg-[#E05D3A] text-white"
            }`}>
            {user?.is_pro ? "Manage" : "Go Pro"} <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        {/* Cancel & refund within 7 days */}
        {proInfo?.refund_eligible && (
          <div className="mt-5 p-4 rounded-xl bg-[#0F2537]/5 border border-dashed border-[#0F2537]/20" data-testid="refund-banner">
            <div className="flex items-start gap-2">
              <ShieldCheck size={16} weight="fill" className="text-[#1E5631] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-[#0F2537]">7-day money-back guarantee active</p>
                <p className="text-xs text-[#0F2537]/70 mt-0.5">
                  {proInfo.days_left_to_refund} day{proInfo.days_left_to_refund === 1 ? "" : "s"} left to cancel + get a full refund of <b>{(proInfo.last_payment?.currency || "fjd").toUpperCase()} {proInfo.last_payment?.amount?.toFixed(2)}</b>.
                </p>
              </div>
            </div>
            <button
              onClick={async () => {
                if (!window.confirm("Cancel Bula Pro and request a refund? Your Pro features will be revoked immediately.")) return;
                setCancelling(true);
                try {
                  const r = await api.post("/pro/cancel");
                  if (r.data.ok) {
                    toast.success(r.data.message);
                    await refreshUser?.();
                    const fresh = await api.get("/pro/me");
                    setProInfo(fresh.data);
                  } else {
                    toast.error(r.data.message);
                  }
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail));
                } finally { setCancelling(false); }
              }}
              disabled={cancelling}
              data-testid="cancel-pro-btn"
              className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs font-bold hover:bg-red-50 transition disabled:opacity-50"
            >
              {cancelling ? "Cancelling…" : "Cancel & refund"}
            </button>
          </div>
        )}
      </div>

      {/* Admin shortcut */}
      {isAdmin && (
        <Link to="/admin" data-testid="profile-admin-link" className="card-interactive p-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0F2537] text-white flex items-center justify-center">
            <ChartBar size={18} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold">Admin dashboard</div>
            <div className="text-xs text-[#0F2537]/60">Revenue, active subs, growth metrics</div>
          </div>
          <ArrowRight size={16} weight="bold" className="text-[#E05D3A]" />
        </Link>
      )}

      <div className="card-default p-7" data-testid="email-prefs">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="label-eyebrow">Email preferences</span>
            <h2 className="font-heading font-bold text-xl mt-1 flex items-center gap-2"><EnvelopeSimple weight="duotone" /> Weekly Bula digest</h2>
            <p className="text-sm text-[#0F2537]/70 mt-1 max-w-md">
              Every Monday: your week's rounds, handicap delta, birdies + a smart summary card. Delivered to {user?.email}.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="opt-out-toggle">
            <input type="checkbox" className="sr-only" checked={!optOut} onChange={toggleOptOut} />
            <span className={`w-12 h-7 rounded-full relative transition ${!optOut ? "bg-[#1E5631]" : "bg-[#0F2537]/20"}`}>
              <span className={`absolute top-0.5 ${!optOut ? "left-[1.4rem]" : "left-0.5"} w-6 h-6 rounded-full bg-white transition shadow`}></span>
            </span>
            <span className="text-sm font-semibold">{!optOut ? "On" : "Off"}</span>
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={openPreview} disabled={busy} data-testid="preview-digest-btn"
            className="px-3 py-2 rounded-lg border border-[#E2E0D8] text-[#0F2537] text-sm font-bold inline-flex items-center gap-1 disabled:opacity-60">
            <Eye size={14} weight="bold" /> Preview this week's digest
          </button>
          <button onClick={sendTest} disabled={busy} data-testid="send-test-digest-btn"
            className="px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-bold inline-flex items-center gap-1 disabled:opacity-60">
            <PaperPlaneTilt size={14} weight="bold" /> Send test to me
          </button>
        </div>
      </div>

      <div className="card-default p-7" data-testid="live-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <span className="label-eyebrow">Live course</span>
            <h2 className="font-heading font-bold text-xl mt-1">Appear on live course</h2>
            <p className="text-sm text-[#0F2537]/70 mt-1 max-w-md">When on, friends playing at the same course as you can see you on their Dashboard's "Live now" widget and send you a Bula. Turn this off to play in private.</p>
          </div>
          <button onClick={toggleLive} disabled={liveBusy} data-testid="toggle-live-btn"
            className={`px-4 py-2.5 rounded-xl font-bold text-sm transition disabled:opacity-50 ${
              liveVisible ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8] text-[#0F2537]"
            }`}>
            {liveBusy ? "…" : liveVisible ? "Visible" : "Hidden"}
          </button>
        </div>
      </div>


      <div className="card-default p-7" data-testid="push-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <span className="label-eyebrow">Notifications</span>
            <h2 className="font-heading font-bold text-xl mt-1 flex items-center gap-2">
              {pushSubbed ? <BellRinging weight="duotone" /> : <BellSlash weight="duotone" />} Tournament push alerts
            </h2>
            <p className="text-sm text-[#0F2537]/70 mt-1 max-w-md">Get a browser notification when a tournament you're in advances, when someone overtakes you, or when results are finalised.</p>
            {!pushSupported && (
              <p className="text-xs text-amber-700 mt-2">
                Not supported on this browser. iPhone users: install Bula Golf to your home screen first, then enable push from there.
              </p>
            )}
            {pushPerm === "denied" && (
              <p className="text-xs text-red-600 mt-2">
                You've blocked notifications for this site — open browser site settings to re-allow.
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <button onClick={togglePush} disabled={pushBusy || !pushSupported || pushPerm === "denied"}
              data-testid="toggle-push-btn"
              className={`px-4 py-2.5 rounded-xl font-bold text-sm transition disabled:opacity-50 ${
                pushSubbed
                  ? "bg-white border border-[#E2E0D8] text-[#0F2537]"
                  : "bg-[#0F2537] text-white hover:brightness-110"
              }`}>
              {pushBusy ? "…" : pushSubbed ? "Turn off" : "Turn on"}
            </button>
            {pushSubbed && (
              <button onClick={testPush} disabled={pushBusy} data-testid="test-push-btn"
                className="text-[11px] font-semibold text-[#E05D3A] hover:underline">
                Send test
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card-default p-7" data-testid="change-password-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <span className="label-eyebrow">Security</span>
            <h2 className="font-heading font-bold text-xl mt-1 flex items-center gap-2"><Lock weight="duotone" /> Change password</h2>
            <p className="text-sm text-[#0F2537]/70 mt-1 max-w-md">Update your sign-in password. You'll stay signed in on this device.</p>
          </div>
          <button onClick={() => setCpOpen((v) => !v)} data-testid="toggle-change-password-btn"
            className="px-4 py-2.5 rounded-xl border border-[#E2E0D8] text-[#0F2537] font-bold text-sm">
            {cpOpen ? "Cancel" : "Change"}
          </button>
        </div>
        {cpOpen && (
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (cpNew.length < 6) { toast.error("New password must be at least 6 characters"); return; }
            if (cpNew !== cpConfirm) { toast.error("Passwords don't match"); return; }
            if (cpNew === cpCurrent) { toast.error("New password must be different"); return; }
            setCpBusy(true);
            const r = await changePassword(cpCurrent, cpNew);
            setCpBusy(false);
            if (r.ok) {
              toast.success("Password updated");
              setCpCurrent(""); setCpNew(""); setCpConfirm(""); setCpOpen(false);
            } else {
              toast.error(r.error);
            }
          }} className="mt-5 space-y-3" data-testid="change-password-form">
            <div>
              <label className="block text-xs font-semibold text-[#0F2537]/70">Current password</label>
              <input type={cpShow ? "text" : "password"} required value={cpCurrent} onChange={(e) => setCpCurrent(e.target.value)}
                data-testid="cp-current-input"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0F2537]/70">New password</label>
              <div className="mt-1 relative">
                <input type={cpShow ? "text" : "password"} required minLength={6} value={cpNew} onChange={(e) => setCpNew(e.target.value)}
                  data-testid="cp-new-input"
                  className="w-full px-4 py-2.5 pr-10 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />
                <button type="button" onClick={() => setCpShow((v) => !v)} data-testid="cp-toggle-show-btn"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0F2537]/40 hover:text-[#0F2537]">
                  {cpShow ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0F2537]/70">Confirm new password</label>
              <input type={cpShow ? "text" : "password"} required minLength={6} value={cpConfirm} onChange={(e) => setCpConfirm(e.target.value)}
                data-testid="cp-confirm-input"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />
            </div>
            <button type="submit" disabled={cpBusy} data-testid="cp-submit-btn"
              className="px-4 py-2.5 rounded-xl bg-[#0F2537] text-white font-bold text-sm hover:bg-[#0F2537]/90 transition disabled:opacity-60">
              {cpBusy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>

      <button onClick={logout} data-testid="profile-logout-btn"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E2E0D8] text-[#0F2537] font-semibold hover:bg-black/5">
        <SignOut size={16} /> Sign out
      </button>
    </div>
  );
}
